import { type Checkpoint } from '../../../shared/sync/schemas';
import { pullWorkspaces, pushWorkspaces } from '../../sync/workspaces';
import { type WorkspaceDoc } from '../collections/workspaces';
import { type ClewDatabase } from '../database';
import {
  checkpointFallbackId,
  withCheckpointFallback,
} from './checkpointFallback';
import { lwwConflictHandler } from './conflictHandler';
import {
  replicateRxCollection,
  type RxReplicationState,
} from 'rxdb/plugins/replication';

const REPLICATION_ID = 'workspaces-trpc';

export const startWorkspacesReplication = (
  db: ClewDatabase,
  userId: string,
): RxReplicationState<WorkspaceDoc, Checkpoint> => {
  const replication = replicateRxCollection<WorkspaceDoc, Checkpoint>({
    autoStart: true,
    collection: db.workspaces,
    deletedField: '_deleted',
    live: true,
    pull: {
      batchSize: 100,
      handler: withCheckpointFallback(
        checkpointFallbackId(userId, REPLICATION_ID),
        async (checkpoint, batchSize) => {
          const result = await pullWorkspaces({
            batchSize,
            checkpoint: checkpoint ?? null,
          });
          return {
            checkpoint: result.checkpoint ?? undefined,
            documents: result.documents.map((doc) => ({
              ...doc,
              _deleted: Boolean(doc._deleted),
            })),
          };
        },
      ),
    },
    push: {
      batchSize: 50,
      handler: async (rows) => {
        const result = await pushWorkspaces(
          rows.map((row) => ({
            assumedMasterState: row.assumedMasterState,
            newDocumentState: row.newDocumentState,
          })),
        );
        return result.conflicts.map((conflict) => ({
          ...conflict,
          _deleted: Boolean(conflict._deleted),
        }));
      },
    },
    replicationIdentifier: REPLICATION_ID,
    retryTime: 5_000,
    waitForLeadership: false,
  });

  db.workspaces.conflictHandler = lwwConflictHandler as never;

  return replication;
};
